import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBookmark,
  faSearch,
  faTrash,
  faSpinner,
  faArrowLeft,
  faFilter,
  faHistory
} from '@fortawesome/free-solid-svg-icons';
import { savedSearchesAPI } from '../services/api';
import { useNotifications } from '../context/NotificationContext';
import Navbar from './Navbar';
import '../assets/css/saved-searches.css';

const SavedSearches = () => {
  const navigate = useNavigate();
  const { showCustomToast } = useNotifications();

  const [savedSearches, setSavedSearches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    fetchSavedSearches();
  }, []);

  const fetchSavedSearches = async () => {
    try {
      setLoading(true);
      const response = await savedSearchesAPI.getSavedSearches();
      if (response && response.data) {
        setSavedSearches(response.data);
      }
    } catch (error) {
      showCustomToast({
        type: 'error',
        title: 'Error',
        message: 'Failed to load saved searches'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApplySearch = (search) => {
    // Navigate to shop with search applied
    navigate('/shop', {
      state: {
        searchQuery: search.query,
        filters: search.filters
      }
    });
  };

  const handleDeleteSearch = async (searchId) => {
    if (!window.confirm('Are you sure you want to delete this saved search?')) {
      return;
    }

    try {
      setActionLoading(searchId);
      await savedSearchesAPI.deleteSavedSearch(searchId);

      // Remove from local state
      setSavedSearches(prev => prev.filter(s => (s._id || s.id) !== searchId));

      showCustomToast({
        type: 'success',
        title: 'Deleted',
        message: 'Saved search removed'
      });
    } catch (error) {
      showCustomToast({
        type: 'error',
        title: 'Error',
        message: 'Failed to delete saved search'
      });
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getFiltersSummary = (filters) => {
    if (!filters) return '';

    const parts = [];
    if (filters.categories && filters.categories.length > 0) {
      parts.push(`${filters.categories.length} categories`);
    }
    if (filters.brands && filters.brands.length > 0) {
      parts.push(`${filters.brands.length} brands`);
    }
    if (filters.priceRange) {
      parts.push('Price filter');
    }

    return parts.length > 0 ? parts.join(', ') : 'No filters';
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="saved-searches-container">
          <div className="saved-searches-loading">
            <FontAwesomeIcon icon={faSpinner} spin size="3x" />
            <p>Loading saved searches...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="saved-searches-container">
        <div className="saved-searches-header">
          <button onClick={() => navigate(-1)} className="saved-searches-back-btn">
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <div className="saved-searches-title">
            <FontAwesomeIcon icon={faBookmark} />
            <h1>Saved Searches</h1>
            <span className="saved-searches-count">{savedSearches.length} saved</span>
          </div>
        </div>

        {savedSearches.length === 0 ? (
          <div className="saved-searches-empty">
            <FontAwesomeIcon icon={faHistory} size="4x" />
            <h2>No saved searches</h2>
            <p>Save your favorite searches to quickly find products you're interested in</p>
            <button onClick={() => navigate('/shop')} className="saved-searches-shop-btn">
              Browse Products
            </button>
          </div>
        ) : (
          <div className="saved-searches-grid">
            {savedSearches.map((search) => {
              const searchId = search._id || search.id;
              const isLoading = actionLoading === searchId;

              return (
                <div key={searchId} className="saved-search-card">
                  <div className="saved-search-header">
                    <div className="saved-search-icon">
                      <FontAwesomeIcon icon={faSearch} />
                    </div>
                    <button
                      onClick={() => handleDeleteSearch(searchId)}
                      className="saved-search-delete"
                      disabled={isLoading}
                      title="Delete saved search"
                    >
                      {isLoading ? (
                        <FontAwesomeIcon icon={faSpinner} spin />
                      ) : (
                        <FontAwesomeIcon icon={faTrash} />
                      )}
                    </button>
                  </div>

                  <div className="saved-search-content">
                    <h3 className="saved-search-query">{search.query}</h3>

                    <div className="saved-search-filters">
                      <FontAwesomeIcon icon={faFilter} />
                      <span>{getFiltersSummary(search.filters)}</span>
                    </div>

                    {search.result_count !== undefined && (
                      <div className="saved-search-results">
                        {search.result_count} results found
                      </div>
                    )}

                    <div className="saved-search-date">
                      Saved on {formatDate(search.created_at)}
                    </div>
                  </div>

                  <button
                    onClick={() => handleApplySearch(search)}
                    className="saved-search-apply-btn"
                    disabled={isLoading}
                  >
                    <FontAwesomeIcon icon={faSearch} />
                    Apply Search
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};

export default SavedSearches;
